import { Card, Row, Col, Statistic, Descriptions, Space, Alert, Table, Tag } from 'antd';
import { BankOutlined, FileTextOutlined, DollarOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import type { NachaFile, ValidationResult } from '@/types/nacha';
import { formatAmount, formatDateTime } from '@/utils/export';

interface SummaryViewProps {
  file: NachaFile;
  validation: ValidationResult;
}

function SummaryView({ file, validation }: SummaryViewProps) {
  // Calculate statistics
  const totalBatches = file.batches.length;
  const totalEntries = file.batches.reduce((sum, batch) => sum + batch.entries.length, 0);
  
  const totalDebit = file.batches.reduce((sum, batch) => {
    const batchDebit = parseInt(batch.control.totalDebitAmount);
    return sum + batchDebit;
  }, 0);
  
  const totalCredit = file.batches.reduce((sum, batch) => {
    const batchCredit = parseInt(batch.control.totalCreditAmount);
    return sum + batchCredit;
  }, 0);
  
  const netAmount = totalCredit - totalDebit;

  // Validation table columns
  const errorColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'error' ? 'red' : 'orange'} icon={type === 'error' ? <CloseCircleOutlined /> : <WarningOutlined />}>
          {type === 'error' ? 'Error' : 'Warning'}
        </Tag>
      ),
    },
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      width: 150,
      render: (field: string) => <code>{field}</code>,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 150,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
    },
  ];

  const allIssues = [...validation.errors, ...validation.warnings];

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '16px' }} className="fade-in">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Validation Status - At the top */}
        {validation.valid ? (
          <Alert
            message="Valid"
            description="No errors found. No warnings found."
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        ) : (
          <Alert
            message="Invalid"
            description={
              <div>
                <div>{validation.errors.length} Error{validation.errors.length !== 1 ? 's' : ''}</div>
                <div>{validation.warnings.length} Warning{validation.warnings.length !== 1 ? 's' : ''}</div>
              </div>
            }
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
          />
        )}

        {/* Errors and Warnings Table - Only show if there are issues */}
        {allIssues.length > 0 && (
          <Card title="Validation Issues" size="small">
            <Table
              columns={errorColumns}
              dataSource={allIssues}
              rowKey={(record, index) => `${record.field}-${index}`}
              pagination={{
                pageSize: 10,
                showTotal: (total) => `Total ${total} issue${total !== 1 ? 's' : ''}`,
              }}
              size="small"
              rowClassName={(record) => (record.type === 'error' ? 'error-row' : 'warning-row')}
            />
        </Card>
        )}

        {/* Statistics Cards */}
        <Card title="Statistics">
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Statistic
                title="Total Batches"
                value={totalBatches}
                prefix={<BankOutlined style={{ color: '#667eea' }} />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Total Entries"
                value={totalEntries}
                prefix={<FileTextOutlined style={{ color: '#667eea' }} />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Total Debit"
                value={`$${formatAmount(totalDebit)}`}
                prefix={<DollarOutlined style={{ color: '#667eea' }} />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Total Credit"
                value={`$${formatAmount(totalCredit)}`}
                prefix={<DollarOutlined style={{ color: '#667eea' }} />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Net Amount"
                value={`$${formatAmount(netAmount)}`}
                prefix={<DollarOutlined style={{ color: '#667eea' }} />}
              />
            </Col>
          </Row>
        </Card>

        {/* File Information */}
        <Card title="File Information">
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Immediate Origin">
              {file.fileHeader.immediateOriginName} ({file.fileHeader.immediateOrigin})
            </Descriptions.Item>
            <Descriptions.Item label="Immediate Destination">
              {file.fileHeader.immediateDestinationName} ({file.fileHeader.immediateDestination})
            </Descriptions.Item>
            <Descriptions.Item label="File Time">
              {formatDateTime(file.fileHeader.fileCreationDate, file.fileHeader.fileCreationTime)}
            </Descriptions.Item>
            <Descriptions.Item label="File Id Modifier">
              {file.fileHeader.fileIdModifier}
            </Descriptions.Item>
            <Descriptions.Item label="Record Size">
              {file.fileHeader.recordSize}
            </Descriptions.Item>
            <Descriptions.Item label="Blocking Factor">
              {file.fileHeader.blockingFactor}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Space>

      <style>{`
        .error-row {
          background-color: rgba(255, 77, 79, 0.08);
        }
        [data-theme='light'] .error-row {
          background-color: #fff2f0;
        }
        .warning-row {
          background-color: rgba(250, 173, 20, 0.08);
        }
        [data-theme='light'] .warning-row {
          background-color: #fffbe6;
        }
      `}</style>
    </div>
  );
}

export default SummaryView;
